import React, { useEffect, useRef, useState } from 'react';
import { X } from '@phosphor-icons/react';

/**
 * Full-screen Street View / 360° panorama overlay.
 *
 * Behaviour
 *  - Looks up the nearest Google panorama within 80m of the given (lat,lng)
 *    using StreetViewService.getPanorama().
 *  - If found, mounts a fresh StreetViewPanorama on its own div (the SDK
 *    needs a live HTMLElement to attach to — we cannot reuse the main
 *    map's pano because that's tied to Pegman and managed by the SDK).
 *  - If no panorama exists at the location (rural / private property),
 *    shows a friendly empty state instead of a silent blank box.
 *
 * Why a modal instead of an inline panel:
 *  - Map drawing UX would suffer if the canvas suddenly shrunk to 50%.
 *  - Users expect to enter "360° mode", inspect, then close — a modal
 *    matches that mental model and dovetails with the existing
 *    HistoricalCompare overlay style.
 *
 * Free feature for everyone (no Pro gate) — Google charges per Street View
 * panorama load; for our usage volumes this is well within free quota.
 */
export function StreetViewPanel({ center, address, onClose }) {
  const containerRef = useRef(null);
  const panoRef = useRef(null);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ok' | 'none'

  useEffect(() => {
    const g = window.google?.maps;
    if (!g || !center) return;

    const svService = new g.StreetViewService();
    svService.getPanorama(
      { location: center, radius: 80, source: g.StreetViewSource.OUTDOOR },
      (data, statusCode) => {
        if (statusCode !== g.StreetViewStatus.OK || !containerRef.current) {
          setStatus('none');
          return;
        }
        try {
          panoRef.current = new g.StreetViewPanorama(containerRef.current, {
            position: data.location.latLng,
            pov: { heading: 0, pitch: 0 },
            zoom: 0,
            // Hide the close button / fullscreen control — we own the chrome.
            disableDefaultUI: false,
            addressControl: true,
            fullscreenControl: false,
            motionTracking: false,
            motionTrackingControl: false,
            linksControl: true,
            panControl: true,
            zoomControl: true,
            enableCloseButton: false,
          });
          setStatus('ok');
        } catch (e) {
          console.warn('StreetViewPanorama init failed:', e);
          setStatus('none');
        }
      }
    );
    // No teardown needed — Google's SDK lets the pano detach with its node.
  }, [center]);

  // Close on Escape — keyboard parity with HistoricalCompare overlay.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 animate-fadeIn"
      data-testid="street-view-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="360 degree Street View"
    >
      <div className="relative w-full max-w-6xl h-[80vh] bg-card rounded-lg shadow-2xl overflow-hidden">
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-4 py-2.5 bg-card/90 backdrop-blur border-b border-border">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider opacity-60">Street View · 360°</div>
            <div className="text-sm font-semibold truncate" data-testid="street-view-address">{address || 'Current location'}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-3 shrink-0 w-8 h-8 rounded-full bg-muted hover:bg-muted/80 inline-flex items-center justify-center transition-colors"
            title="Close Street View (Esc)"
            data-testid="street-view-close"
            aria-label="Close Street View"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <div ref={containerRef} className="absolute inset-0 pt-12" data-testid="street-view-canvas" />

        {status === 'loading' && (
          <div className="absolute inset-0 pt-12 flex items-center justify-center text-sm opacity-60 pointer-events-none">
            Loading 360° view…
          </div>
        )}
        {status === 'none' && (
          <div className="absolute inset-0 pt-12 flex flex-col items-center justify-center gap-2 text-center px-6">
            <div className="text-base font-semibold">No Street View available here</div>
            <div className="text-xs opacity-70 max-w-md">
              Google doesn&apos;t have 360° coverage within 80&nbsp;m of this address — common for rural lots, gated estates and very recent developments.
            </div>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 px-4 py-1.5 text-xs font-bold uppercase tracking-wider rounded bg-primary text-white hover:bg-primary/90 transition-colors"
              data-testid="street-view-none-close"
            >
              Back to map
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
